import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Box, Image, Stack, Typography } from '@packages/shared';
import { CompactProductFragmentFragmentDoc } from '@packages/gql/generated/shopping/CompactProductFragmentFragmentDoc';
import { Price } from '@packages/modules';
import { useResponsiveAkamaiTemplate } from '@packages/modules/src/hooks/useResponsiveAkamaiTemplate/useResponsiveAkamaiTemplate';
import { TaxHint } from '../TaxHint';

/* GraphQL */ `
  fragment CompactProductFragment on Product {
    name
    price {
      ...ProductPriceFragment
    }
    media {
      type
      url
    }
    delivery {
      ...TaxHintFragment
    }
  }
`;

export type CompactProductProps = {
  /**
   * Price, media and deliveryType **data** from given product.
   */
  data: FragmentType<typeof CompactProductFragmentFragmentDoc>;
  /**
   * Whether to show product name
   * Shows maximal two lines of the name
   * @default false
   */
  withProductName?: boolean;

  /**
   * Whether to show tax hint
   * @default true
   */
  showTaxHint?: boolean;
};

/**
 * CompactProduct component displays the Price and Image of a given product in a condensed layout.
 * */
export const CompactProduct = ({
  data: compactProductData,
  withProductName = false,
  showTaxHint = true,
}: CompactProductProps) => {
  const { media, name, price, delivery } = getFragmentData(
    CompactProductFragmentFragmentDoc,
    compactProductData,
  );
  const { templateName, imageDimensions } = useResponsiveAkamaiTemplate('compact');

  /**
   * URL of the first image in the media array.
   * TODO: check if showing the 1st image is the expected behavior -> https://empiriecom.atlassian.net/browse/INFORM-2593
   */
  const imageUrl = media.find((ele) => ele.type === 'IMAGE')?.url;

  return (
    <Stack direction="row" gap={1.5} alignItems="flex-start">
      <Box
        sx={{
          height: { xs: '4.5rem', lg: '5.5rem' },
          width: { xs: '4.5rem', lg: '5.5rem' },
          position: 'relative',
        }}
      >
        {imageUrl && (
          <Image
            src={`${imageUrl}?${templateName}`}
            unoptimized
            alt={name}
            style={{
              objectFit: 'contain',
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
            }}
            {...imageDimensions}
          />
        )}
      </Box>
      <Stack>
        {withProductName && (
          <Typography
            variant="h4"
            component="p"
            sx={{
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              fontWeight: 'bold',
              mb: 0.5,
              color: 'text.dark',
            }}
          >
            {name}
          </Typography>
        )}
        <Price data={price} />
        {showTaxHint && <TaxHint data={delivery} />}
      </Stack>
    </Stack>
  );
};
